import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Badge, DataTable, Tag, ActionButton } from "./ui";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem, MDBTabs, MDBTabsItem, MDBTabsLink, MDBPagination, MDBPaginationItem, MDBPaginationLink, MDBIcon, MDBTooltip } from "mdb-react-ui-kit";
import { api } from "../services";
import { RotateCw } from "lucide-react";
import CatalogModal from "../components/Modals/CatalogModal";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SortDropdown from "./dropdown/SortDropdown";
import ItemsPerPageDropdown from "./dropdown/ItemsPerPageDropdown";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DeleteModal from "./Modals/DeleteModal";
import {
  isPosReadOnlyEntity,
  isPosIntegrationDeleteLocked,
  posReadOnlyTooltip,
  posDeleteLockedTooltip,
} from "../utils/posReadOnly";
import { usePermission } from "../hooks/usePermission";
import {
  RBAC_MODULES,
  PERMISSION_ACTIONS,
} from "../config/routePermissions";

const getCatalogSortApiField = (column, isArabic) => {
  const map = {
    name: isArabic ? "name_ar" : "name_en",
    store: "store_id",
    createdAt: "createdAt",
  };
  return map[column];
};

/** Raw message from `response.data` (string or { message, error }) */
function getRawApiMessageFromResponseData(data) {
  if (data == null) return "";
  if (typeof data === "string") return data.trim();
  if (typeof data === "object") {
    if (data.message) return String(data.message).trim();
    if (typeof data.error === "string") return data.error.trim();
  }
  return "";
}

/** Map backend English API messages to the active locale; unknown text passes through. */
function translateServerMessage(msg, t) {
  if (msg == null) return "";
  const s = String(msg).trim();
  if (!s) return "";
  return t(s, { defaultValue: s });
}

function getRawApiErrorMessage(error) {
  return getRawApiMessageFromResponseData(error?.response?.data);
}

/** TabSense refresh errors: show API `message` and `error` (detail) when both differ. */
function formatTabsenseRefreshErrorForToast(error, t) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    const raw = getRawApiErrorMessage(error);
    return raw ? translateServerMessage(raw, t) : t("tabSenseRefreshFailed");
  }
  const main = data.message
    ? translateServerMessage(String(data.message), t)
    : "";
  const extra =
    typeof data.error === "string" && data.error.trim()
      ? translateServerMessage(data.error.trim(), t)
      : "";
  if (main && extra && extra !== main) {
    return `${main} — ${extra}`;
  }
  return main || extra || t("tabSenseRefreshFailed");
}

function resolveCatalogStoreId(catalog) {
  if (catalog?.store_id) return String(catalog.store_id);
  if (catalog?.store?._id) return String(catalog.store._id);
  return null;
}

function isActiveTabSenseAppIntegration(app) {
  if (!app) return false;
  const name = String(app.application || "").toLowerCase();
  if (name !== "tabsense") return false;
  return String(app.status || "").toLowerCase() === "connected";
}

function canTabSenseMenuRefresh(catalog, activeTabSenseStoreIds) {
  if (!catalog?.is_tabsense) return false;
  const storeId = resolveCatalogStoreId(catalog);
  if (!storeId || !activeTabSenseStoreIds?.has(String(storeId))) return false;
  return !!(catalog.tabsenseIntegrationId && catalog.tabsense_id);
}

function getCatalogDisplayName(catalog, isArabic) {
  return isArabic
    ? catalog.name_ar || catalog.name
    : catalog.name_en || catalog.name;
}

function getCatalogDisplayDescription(catalog, isArabic, t) {
  const raw = isArabic
    ? catalog?.description_ar || catalog?.description || catalog?.description_en
    : catalog?.description_en || catalog?.description || catalog?.description_ar;
  const trimmed = raw != null ? String(raw).trim() : "";
  return trimmed || t("No description");
}

/** @returns {(number|'ellipsis')[]} */
function getCatalogPaginationItems(page, totalPages) {
  if (!totalPages || totalPages < 1) return [];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const delta = 1;
  const pageSet = new Set([1, totalPages]);
  for (let i = page - delta; i <= page + delta; i += 1) {
    if (i >= 1 && i <= totalPages) pageSet.add(i);
  }
  const sorted = [...pageSet].sort((a, b) => a - b);
  const out = [];
  let prev;
  for (const num of sorted) {
    if (prev !== undefined) {
      if (num - prev === 2) {
        out.push(prev + 1);
      } else if (num - prev > 2) {
        out.push("ellipsis");
      }
    }
    out.push(num);
    prev = num;
  }
  return out;
}

function CatalogPaginationFooter({
  show,
  t,
  isRtl = false,
  paginationItems,
  currentPage,
  pagination,
  itemsPerPage,
  handleItemsPerPageChange,
  handlePageChange,
}) {
  if (!show) return null;

  return (
    <div
      className="catalog-pagination-bar catalog-data-panel__pagination"
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="catalog-pagination-bar__summary d-flex align-items-center flex-wrap gap-2">
        <span className="catalog-pagination-bar__label text-muted small">
          {t("show")}
        </span>
        <ItemsPerPageDropdown
          itemsPerPage={itemsPerPage}
          onChange={handleItemsPerPageChange}
          displayMode="compact"
          height="36px"
          className="catalog-pagination-ippx"
        />
        <span className="catalog-pagination-bar__total text-muted small">
          {t("catalogOfTotalResults", {
            count: pagination.total ?? 0,
          })}
        </span>
      </div>
      {pagination.totalPages > 0 ? (
        <nav
          className="catalog-pagination-bar__pages"
          aria-label={t("pagination", { defaultValue: "Pagination" })}
        >
          <MDBPagination className="mb-0 catalog-pagination-page-list">
            <MDBPaginationItem disabled={currentPage <= 1}>
              <MDBPaginationLink
                tag="button"
                type="button"
                aria-label={t("previous")}
                className="catalog-pagination-page-btn"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) handlePageChange(currentPage - 1);
                }}
              >
                <MDBIcon fas icon={isRtl ? "chevron-right" : "chevron-left"} />
              </MDBPaginationLink>
            </MDBPaginationItem>
            {paginationItems.map((item, idx) =>
              item === "ellipsis" ? (
                <MDBPaginationItem
                  key={`ellipsis-${idx}`}
                  disabled
                  className="catalog-pagination-ellipsis"
                >
                  <span className="page-link border-0 bg-transparent catalog-pagination-page-ellipsis">
                    …
                  </span>
                </MDBPaginationItem>
              ) : (
                <MDBPaginationItem key={item} active={item === currentPage}>
                  <MDBPaginationLink
                    tag="button"
                    type="button"
                    className="catalog-pagination-page-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      if (item !== currentPage) handlePageChange(item);
                    }}
                  >
                    {item}
                  </MDBPaginationLink>
                </MDBPaginationItem>
              )
            )}
            <MDBPaginationItem
              disabled={
                currentPage >= pagination.totalPages ||
                pagination.totalPages < 1
              }
            >
              <MDBPaginationLink
                tag="button"
                type="button"
                aria-label={t("next")}
                className="catalog-pagination-page-btn"
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < pagination.totalPages) {
                    handlePageChange(currentPage + 1);
                  }
                }}
              >
                <MDBIcon fas icon={isRtl ? "chevron-left" : "chevron-right"} />
              </MDBPaginationLink>
            </MDBPaginationItem>
          </MDBPagination>
        </nav>
      ) : null}
    </div>
  );
}

const FOODICS_ICON_SRC = `${process.env.PUBLIC_URL || ""}/images/foodics-icon.svg`;
const TABSENSE_ICON_SRC = `${process.env.PUBLIC_URL || ""}/images/tabsense-icon.svg`;

/**
 * Unified catalog title: teal “stack” glyph + optional Foodics / TabSense logos.
 * Loop-native catalogs show glyph + name only (no POS image).
 */
function CatalogRowTitle({ catalog, isArabic, t, variant = "table" }) {
  const name = getCatalogDisplayName(catalog, isArabic);
  const isFoodics = !!catalog.is_foodics;
  const isTabSense = !!catalog.is_tabsense;
  const isPartner = !!catalog.is_partner;
  const posSummary = isPartner
    ? t("managedByPartnerSystem")
    : isFoodics
      ? t("posTypeFoodics")
      : isTabSense
        ? t("posTypeTabSense")
        : "";
  const tip = posSummary ? `${posSummary}: ${name}` : name;

  return (
    <div
      className={`catalog-row-title catalog-row-title--${variant}`}
      title={tip}
    >
     
      {isPartner ? (
        <span
          className="catalog-row-title__partner-badge text-muted small"
          title={t("managedByPartnerSystem")}
        >
          {t("managedByPartnerSystem")}
        </span>
      ) : isFoodics ? (
        <img
          className="catalog-row-title__pos-icon"
          src={FOODICS_ICON_SRC}
          alt=""
          aria-label={t("posTypeFoodics")}
          width={14}
          height={14}
        />
      ) : isTabSense ? (
        <img
          className="catalog-row-title__pos-icon"
          src={TABSENSE_ICON_SRC}
          alt=""
          aria-label={t("posTypeTabSense")}
          width={14}
          height={14}
        />
      ) : (
        <span
          className="catalog-row-title__pos-spacer"
          aria-hidden
        />
      )}
      <span className="catalog-row-title__text">{name}</span>
    </div>
  );
}

function Catalog() {
  const {t,i18n} = useTranslation("translation")
  const isArabic = i18n.language === "ar";
  const { can, isStaff } = usePermission();
  const canAddCatalog = can(RBAC_MODULES.CATALOG, PERMISSION_ACTIONS.ADD);
  const canEditCatalog = can(RBAC_MODULES.CATALOG, PERMISSION_ACTIONS.EDIT);
  const canDeleteCatalog = can(RBAC_MODULES.CATALOG, PERMISSION_ACTIONS.DELETE);
  const [activeTab, setActiveTab] = useState("All");
  console.log("activeTab", activeTab);

  const [viewMode, setViewMode] = useState("list");
  const [catalogs, setCatalogs] = useState([]);
  const [filteredCatalogs, setFilteredCatalogs] = useState([]);
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("Select App");
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({
    totalCatalogs: 0,
  });

  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("recent"); // Sorting order
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [catalogSortField, setCatalogSortField] = useState("createdAt");
  const [catalogSortDirection, setCatalogSortDirection] = useState("desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [resp, setResponse] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [catalogToDelete, setCatalogToDelete] = useState("");
  const [selectedCatalog2, setSelectedCatalog2] = useState(null);
  const [modalOpen2, setModalOpen2] = useState(false);
  const [refreshingMenuCatalogId, setRefreshingMenuCatalogId] = useState(null);
  const [activeTabSenseStoreIdSet, setActiveTabSenseStoreIdSet] = useState(
    () => new Set()
  );

  const storeIdsInCatalogsKey = useMemo(() => {
    const ids = [
      ...new Set(
        (catalogs || [])
          .map((c) => resolveCatalogStoreId(c))
          .filter(Boolean)
          .map(String)
      ),
    ].sort();
    return ids.join(",");
  }, [catalogs]);

  useEffect(() => {
    const ids = storeIdsInCatalogsKey
      ? storeIdsInCatalogsKey.split(",").filter(Boolean)
      : [];
    if (ids.length === 0) {
      setActiveTabSenseStoreIdSet(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem("token");
      const withActive = new Set();
      await Promise.all(
        ids.map(async (sid) => {
          try {
            const { data } = await api.get(`/stores/${sid}/apps`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const apps = data?.apps || [];
            if (apps.some(isActiveTabSenseAppIntegration)) {
              withActive.add(sid);
            }
          } catch (err) {
            console.error(
              "Error checking TabSense app integration for store",
              sid,
              err
            );
          }
        })
      );
      if (!cancelled) {
        setActiveTabSenseStoreIdSet(withActive);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeIdsInCatalogsKey]);

  const skipInitialPaginationFetch = useRef(true);
  const skipInitialSortFetch = useRef(true);
  const skipInitialDateFilterFetch = useRef(true);
  const skipInitialSearchFetch = useRef(true);

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setCatalogToDelete("");
  };
  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch); // ✅ This is the missing piece
    setSelectedBranchId(branch._id);
  };
     const handleDeleteCatalog = async () => {
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/v2.1/catalog/${catalogToDelete}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      toast.success(t("Catalog deleted successfully."));
      fetchCatalogs();
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting catalog:", error);
      const raw = getRawApiErrorMessage(error);
      toast.error(
        raw ? translateServerMessage(raw, t) : t("Failed to delete catalog.")
      );
      setShowDeleteModal(false);
    }
  };
  useEffect(() => {
    fetchStoreId();
    fetchCatalogs(null, true);
  }, []);

  useEffect(() => {
    if (storeId) {
      fetchBranches();
    }
    // eslint-disable-next-line
  }, [storeId]);

  useEffect(() => {
    filterCatalogs();
    console.log("filter catalogs", filteredCatalogs);
  }, [
    selectedBranchId,
    activeTab,
    sortOrder,
    catalogs,
  ]);

    useEffect(() => {
    if (skipInitialSearchFetch.current) {
      skipInitialSearchFetch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCatalogs(null, false);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (skipInitialPaginationFetch.current) {
      skipInitialPaginationFetch.current = false;
      return;
    }
    fetchCatalogs(); // Refetch when pagination changes
  }, [currentPage, itemsPerPage]);

  // const handleCreateCatalog = async (data) => {
  //   try {
  //     console.log("Catalog Created:", data);
  //     const payload = {
  //       name: data.name,
  //       description: data.description,
  //       date: data.date,
  //       branchId: data.branchId,
  //     };

  //     const token = localStorage.getItem("token");
  //     const response = await api.post("catalog", payload, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     setResponse(response);
  //     toast.success(response.data.message || `${t("Catalog created successfully.")}`);
  //     fetchCatalogs();
  //     // Show success notification
  //     // setNotification({
  //     //   show: true,
  //     //   message: "Catalog created successfully!",
  //     //   type: "success",
  //     // });

  //     // // Auto-hide after 5 seconds
  //     // setTimeout(
  //     //   () => setNotification({ show: false, message: "", type: "success" }),
  //     //   5000
  //     // );
  //   } catch (error) {
  //     console.error(
  //       "Create Catalog Error:",
  //       error.response?.data || error.message
  //     );
  //     setError("Error while creating new catalog");
  //     toast.error(error.response?.data?.message || `${t("Error while creating new catalog")}`);
  //     // Show error notification
  //     // setNotification({
  //     //   show: true,
  //     //   message:
  //     //     error.response?.data?.message || "Error while creating new catalog",
  //     //   type: "error",
  //     // });

  //     // // Auto-hide after 5 seconds
  //     // setTimeout(
  //     //   () => setNotification({ show: false, message: "", type: "error" }),
  //     //   5000
  //     // );
  //   }
  // };

  const fetchStoreId = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.data?.stores?.length) {
        throw new Error("No stores found.");
      }

      const firstStoreId = response.data.stores[0]._id;
      setStoreId(firstStoreId);
      
      if (isStaff) {
        console.log(`[Catalog] Staff user - using first accessible store for branch dropdown: ${firstStoreId}`);
      } else {
        console.log(`[Catalog] Non-staff user - using first store: ${firstStoreId}`);
      }
    } catch (error) {
      console.error(
        "Error fetching store ID:",
        error.response?.data || error.message
      );
      setError(`${t("Failed to fetch store.")}`);

      // Show error notification
      const storeErrRaw = getRawApiErrorMessage(error);
      setNotification({
        show: true,
        message: storeErrRaw
          ? translateServerMessage(storeErrRaw, t)
          : t("Failed to fetch store."),
        type: "error",
      });

      // Auto-hide after 5 seconds
      setTimeout(
        () => setNotification({ show: false, message: "", type: "error" }),
        5000
      );
    }
  };

  // Fetch Branches for Selected Store
  const fetchBranches = async () => {
    if (!storeId) return;

    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBranches(response.data.branches || []);
    } catch (error) {
      console.error(
        "Error fetching branches:",
        error.response?.data || error.message
      );
      setError(`${t("Failed to fetch branches.")}`);

      // Show error notification
      const branchErrRaw = getRawApiErrorMessage(error);
      setNotification({
        show: true,
        message: branchErrRaw
          ? translateServerMessage(branchErrRaw, t)
          : t("Failed to fetch branches."),
        type: "error",
      });

      // Auto-hide after 5 seconds
      setTimeout(
        () => setNotification({ show: false, message: "", type: "error" }),
        5000
      );
    }
  };

  const filterCatalogs = () => {
    let updatedCatalogs = [...catalogs];

    // Filter by selected branch
    if (selectedBranchId) {
      updatedCatalogs = updatedCatalogs.filter(
        (catalog) => catalog.branch_id === selectedBranchId
      );
    }

    setFilteredCatalogs(updatedCatalogs);
  };

  const fetchCatalogs = async (
    appId = null,
    full = false,
    sortBy = catalogSortField,
    sortOrderParam = catalogSortDirection
  ) => {
      if (full) {
      setLoadingCatalogs(true);
    } else {
      setTableLoading(true);
    }
    try {
      const token = localStorage.getItem("token");
      
      if (isStaff) {
        console.log("[Catalog] Staff user - backend middleware will handle store/branch filtering automatically");
      } else {
        console.log("[Catalog] Non-staff user - backend middleware will apply appropriate filtering");
      }
      
      const response = await api.get("/v2.1/catalog", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: (searchQuery || "").trim() || undefined,
          sortBy,
          sortOrder: sortOrderParam,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
      });

      console.log(response.data.data);

      const catalogs = response.data.data;
      const paginationData = response.data.pagination;

      setCatalogs(catalogs);
      setFilteredCatalogs(catalogs); // Initialize filtered catalogs
      setPagination(paginationData);
      calculateStats(catalogs);
    } catch (err) {
      console.error("Error fetching catalogs:", err.message);

      // Show error notification
      const listErrRaw = getRawApiErrorMessage(err);
      setNotification({
        show: true,
        message: listErrRaw
          ? translateServerMessage(listErrRaw, t)
          : t("Failed to fetch catalogs."),
        type: "error",
      });

      // Auto-hide after 5 seconds
      setTimeout(
        () => setNotification({ show: false, message: "", type: "error" }),
        5000
      );
    } finally {
      if (full) {
        setLoadingCatalogs(false);
      } else {
        setTableLoading(false);
      }
    }
  };
  
   useEffect(() => {
    if (skipInitialSortFetch.current) {
      skipInitialSortFetch.current = false;
      return;
    }
    const direction = sortOrder === "recent" ? "desc" : "asc";
    setTableSortColumn("createdAt");
    setCatalogSortField("createdAt");
    setCatalogSortDirection(direction);
    setCurrentPage(1);
    setTableLoading(true);
    fetchCatalogs(null, false, "createdAt", direction);
  }, [sortOrder]);

  useEffect(() => {
    if (skipInitialDateFilterFetch.current) {
      skipInitialDateFilterFetch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCatalogs(null, false);
    }, 600);
    return () => clearTimeout(timer);
  }, [dateFrom, dateTo]);

  const calculateStats = (catalogs = []) => {
    setStats({ totalCatalogs: catalogs.length });
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleAppSelect = (app) => {
    setSelectedApp(app.application);
    fetchCatalogs(app._id); // Fetch catalogs for the selected app
  };

  const handleViewModeChange = (mode) => setViewMode(mode);

  const handleView = (catalog) => {
    console.log("Edit Item Click", catalog);
    navigate(`/catalog/${catalog._id}`);
  };

  const handleTabSenseMenuRefresh = async (catalog) => {
    if (!canTabSenseMenuRefresh(catalog, activeTabSenseStoreIdSet)) {
      toast.error(t("tabSenseRefreshUnavailable"));
      return;
    }
    if (refreshingMenuCatalogId) {
      return;
    }
    const storeIdVal = resolveCatalogStoreId(catalog);
    const payload = {
      storeId: storeIdVal,
      integrationId: String(catalog.tabsenseIntegrationId),
      tabSenseMenuId: String(catalog.tabsense_id),
    };
    setRefreshingMenuCatalogId(catalog._id);
    toast.info(t("tabSenseSyncStartedToast"), { autoClose: 4500 });
    try {
      const { data } = await api.post("/tabsense-menu/refresh", payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 900000,
      });
      const successRaw = data?.message;
      const msg = successRaw
        ? translateServerMessage(successRaw, t)
        : t("tabSenseMenuRefreshCompleted");
      toast.success(msg);
      fetchCatalogs(null, false);
    } catch (error) {
      toast.error(formatTabsenseRefreshErrorForToast(error, t));
    } finally {
      setRefreshingMenuCatalogId(null);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setTableLoading(true);
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (limit) => {
 setTableLoading(true);
    setItemsPerPage(limit);
    setCurrentPage(1); 
  };

  /** Page indices and `"ellipsis"` tokens for numbered footer (< 1 2 … last >). */
  const catalogPaginationItems = getCatalogPaginationItems(
    currentPage,
    pagination.totalPages
  );

  const renderCatalogRowActions = (catalog) => (
    <div
      className="d-flex flex-nowrap gap-2 align-items-center justify-content-start oh-table-actions catalog-action-btns"
      onClick={(e) => e.stopPropagation()}
    >
      {canTabSenseMenuRefresh(catalog, activeTabSenseStoreIdSet) && (
        <ActionButton
          variant="refresh"
          icon={<RotateCw size={13} strokeWidth={2} aria-hidden="true" />}
          title={
            refreshingMenuCatalogId === catalog._id
              ? t("tabSenseRefreshProcessing")
              : t("refresh")
          }
          disabled={!!refreshingMenuCatalogId}
          aria-busy={refreshingMenuCatalogId === catalog._id}
          onClick={() => handleTabSenseMenuRefresh(catalog)}
          aria-label={t("refresh")}
        >
          {refreshingMenuCatalogId === catalog._id
            ? t("tabSenseRefreshProcessing")
            : t("refresh")}
        </ActionButton>
      )}
      {isPosReadOnlyEntity(catalog) ? (
        <MDBTooltip tag="span" title={posReadOnlyTooltip(t, catalog)}>
          <span className="d-inline-flex">
            <ActionButton variant="view" disabled>
              {t("view")}
            </ActionButton>
          </span>
        </MDBTooltip>
      ) : (
        <ActionButton variant="view" onClick={() => handleView(catalog)}>
          {t("view")}
        </ActionButton>
      )}
      {isPosReadOnlyEntity(catalog) ? (
        <MDBTooltip tag="span" title={posReadOnlyTooltip(t, catalog)}>
          <span className="d-inline-flex">
            <ActionButton variant="edit" disabled>
              {t("edit")}
            </ActionButton>
          </span>
        </MDBTooltip>
      ) : !canEditCatalog ? (
        <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
          <span className="d-inline-flex">
            <ActionButton variant="edit" disabled>
              {t("edit")}
            </ActionButton>
          </span>
        </MDBTooltip>
      ) : (
        <ActionButton
          variant="edit"
          onClick={() => {
            setSelectedCatalog2(catalog);
            setModalOpen2(true);
          }}
        >
          {t("edit")}
        </ActionButton>
      )}
      {isPosIntegrationDeleteLocked(catalog) ? (
        <MDBTooltip tag="span" title={posDeleteLockedTooltip(t, catalog)}>
          <span className="d-inline-flex">
            <ActionButton variant="delete" disabled>
              {t("delete")}
            </ActionButton>
          </span>
        </MDBTooltip>
      ) : !canDeleteCatalog ? (
        <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
          <span className="d-inline-flex">
            <ActionButton variant="delete" disabled>
              {t("delete")}
            </ActionButton>
          </span>
        </MDBTooltip>
      ) : (
        <ActionButton
          variant="delete"
          onClick={() => {
            setCatalogToDelete(catalog._id);
            setShowDeleteModal(true);
          }}
        >
          {t("delete")}
        </ActionButton>
      )}
    </div>
  );

  const catalogListColumns = useMemo(
    () => [
      {
        id: "name",
        header: t("catalogName"),
        strong: true,
        sortable: true,
        width: 220,
        render: (catalog) => (
          <CatalogRowTitle
            catalog={catalog}
            isArabic={isArabic}
            t={t}
            variant="table"
          />
        ),
      },
      {
        id: "description",
        header: t("description"),
        width: 220,
        render: (catalog) =>
          getCatalogDisplayDescription(catalog, isArabic, t),
      },
      {
        id: "store",
        header: t("store"),
        sortable: true,
        width: 160,
        render: (catalog) => catalog.store?.name || "-",
      },
      {
        id: "item_count",
        header: t("itemCount"),
        width: 100,
        render: (catalog) => catalog.item_count,
      },
      {
        id: "createdAt",
        header: t("createdAtCatalog"),
        sortable: true,
        width: 130,
        render: (catalog) =>
          catalog.createdAt
            ? new Date(catalog.createdAt).toLocaleDateString("en-GB")
            : "-",
      },
      {
        id: "actions",
        header: t("actions"),
        width: 320,
        render: (catalog) => renderCatalogRowActions(catalog),
      },
    ],
    [
      t,
      isArabic,
      canEditCatalog,
      canDeleteCatalog,
      refreshingMenuCatalogId,
      activeTabSenseStoreIdSet,
    ]
  );

  const applyTableSort = (column, direction) => {
    const apiField = getCatalogSortApiField(column, isArabic);
    if (!apiField) return;

    setTableSortColumn(column);
    setCatalogSortField(apiField);
    setCatalogSortDirection(direction);
    setCurrentPage(1);
    setTableLoading(true);

    if (column === "createdAt") {
      const nextSortOrder = direction === "desc" ? "recent" : "oldest";
      if (nextSortOrder !== sortOrder) {
        setSortOrder(nextSortOrder);
        return;
      }
    }

    fetchCatalogs(null, false, apiField, direction);
  };

  return (
    <div className="catalog-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
         {loadingCatalogs && !tableLoading ? (
          <>
            <Skeleton height={40} width={220} style={{ marginBottom: 32 }} />
            <MDBRow className="mb-4">
              <MDBCol md="6">
                <Skeleton height={100} borderRadius={12} />
              </MDBCol>
            </MDBRow>
            <MDBRow className="align-items-center mb-4 catalog-filter-row">
              <MDBCol md="2" xs="12" className="mb-3 mb-md-0">
                <Skeleton height={36} width={180} />
              </MDBCol>
              <MDBCol md="2" xs="12">
                <Skeleton height={36} width={180} />
              </MDBCol>
              <MDBCol md="3" xs="12">
                <Skeleton height={36} width={240} />
              </MDBCol>
              <MDBCol md="2">
                <Skeleton height={36} width={120} />
              </MDBCol>
              <MDBCol md="3" className="text-end">
                <Skeleton height={36} width={120} />
                <Skeleton height={36} width={120} style={{ marginLeft: 8 }} />
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol md="12">
                <Skeleton
                  height={48}
                  style={{ marginBottom: 8, borderRadius: 8 }}
                  count={8}
                />
              </MDBCol>
            </MDBRow>
          </>
        ) : (
          <>
            {/* Notification Popup */}
            {notification.show && (
              <div
                className={`alert alert-${
                  notification.type === "success" ? "success" : "danger"
                } alert-dismissible fade show position-fixed`}
                style={{
                  top: "20px",
                  right: "20px",
                  zIndex: 9999,
                  minWidth: "300px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
                role="alert"
              >
                <div className="d-flex align-items-center">
                  <MDBIcon
                    icon={
                      notification.type === "success"
                        ? "check-circle"
                        : "exclamation-triangle"
                    }
                    className={`me-2 ${
                      notification.type === "success"
                        ? "text-success"
                        : "text-danger"
                    }`}
                  />
                  <span className="fw-semibold">
                    {notification.type === "success" ? t("Success!") : t("Error!")}
                  </span>
                </div>
                <div className="mt-1">{notification.message}</div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setNotification({
                      show: false,
                      message: "",
                      type: "success",
                    })
                  }
                  aria-label="Close"
                ></button>
              </div>
            )}

            <div className="oh-page-header oh-page-header--split">
              <h1 className="page-title-header">{t("catalog")}</h1>
              <div className="d-flex align-items-center gap-2 flex-nowrap">
                <Tag variant="info">
                  {t("totalCatalogs")}: {pagination.total}
                </Tag>
              </div>
            </div>

            <div className="oh-page-section">
            <Toolbar
              hideIcons={true}
              searchPlaceHolder={t("Search Catalog")}
              showSearch={true}
              searchQuery={searchQuery}
              handleSearch={handleSearch}
               showDateRange={true}
              dateFrom={dateFrom}
              onDateFromChange={(e) => setDateFrom(e.target.value)}
              dateTo={dateTo}
              onDateToChange={(e) => setDateTo(e.target.value)}
              showSort={true}
              selectedSort={sortOrder}
              setSelectedSort={setSortOrder}
              sortOrders={() => {}}
              // showFilter={true}
              orderStatuses={[
                { id: "all", name: `${t("all")}` },
                { id: "synced", name: `${t("synced")}` },
                { id: "notSynced", name: `${t("notsynced")}` },
              ]}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              showViewMode={true}
              viewMode={viewMode}
              handleViewChange={setViewMode}
              // showItemsPerPage={true}
              // itemsPerPage={itemsPerPage}
              // handleItemsPerPageChange={handleItemsPerPageChange}
            />
            </div>
            <CatalogModal
              show={Boolean(modalOpen && canAddCatalog)}
              setShow={setModalOpen}
              branches={branches}
              onCatalogUpdate={() => {
                setPagination((prev) => ({
                  ...prev,
                  page: 1, // reset to first page
                }));
                fetchCatalogs();
              }}
            />

            <div className="container-fluid px-0">
              <div className="row g-3 align-items-center mb-4">
                <div className="col-12 col-xl-9">
                  <div className="d-flex flex-wrap gap-3">

                    {/* <div className="flex-fill" style={{ minWidth: "200px" }}>
          <input
            type="text"
            placeholder="Search orders"
            value={searchQuery}
            onChange={handleSearch}
            className="form-control"
            style={{
              fontSize: "14px",
              backgroundColor: "#f9f9f9",
              width: "100%",
              height: "40px"
            }}
          />
        </div> */}

                    {/* <div className="flex-fill" style={{ minWidth: "300px" }}>
          <SortDropdown
            selectedSort={sortOrder}
            setSelectedSort={setSortOrder}
            sortOrders={() => {}}
            height="40px"
          />
        </div> */}
                  </div>
                </div>

                {/* <div className="col-12 col-xl-3">
      <div className="d-flex justify-content-end gap-2">
        <Button
          color="light"
          onClick={() => setViewMode("list")}
          active={viewMode === "list"}
          style={{
            minWidth: "80px",
            height: "40px",
            backgroundColor: viewMode === "list" ? "#e0e0e0" : "#ffffff",
          }}
        >
          List
        </Button>
        <Button
          color="light"
          onClick={() => setViewMode("grid")}
          active={viewMode === "grid"}
          style={{
            minWidth: "80px",
            height: "40px",
            backgroundColor: viewMode === "grid" ? "#e0e0e0" : "#ffffff",
          }}
        >
          Grid
        </Button>
      </div>
    </div> */}
              </div>
            </div>

            <div className="mb-3">
              <div
                className="d-flex align-items-center justify-content-between flex-nowrap"
                style={{ width: "100%", gap: "10px" }}
              >
                <div
                  className="w-100"
                  style={{
                    maxWidth: "251px",
                  }}
                >
                  {/* <div className="d-block d-sm-none">
          <ItemsPerPageDropdown
            itemsPerPage={itemsPerPage}
            onChange={handleItemsPerPageChange}
            size="sm"  />
        </div>
        <div className="d-none d-sm-block">
          <ItemsPerPageDropdown
            itemsPerPage={itemsPerPage}
            onChange={handleItemsPerPageChange}
          />
        </div> */}
                </div>

                {/* <div
        style={{
        
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setModalOpen(true)}
          className="btn btn-primary w-auto d-none d-sm-inline-block"
          style={{ whiteSpace: "nowrap" }}
        >
          New&nbsp;Catalog
        </button>
        <button
          onClick={() => setModalOpen(true)}
          className="btn btn-sm btn-primary w-auto d-inline-block d-sm-none"
          style={{ whiteSpace: "nowrap" }}
        >
          + Catalog
        </button>

        
      </div> */}
              </div>
            </div>

            {viewMode === "grid" ? (
              <div className="catalog-data-panel">
                <MDBRow className="g-4 catalog-data-panel__grid">
                  {tableLoading && (
                    [...Array(8)].map((_, idx) => (
                      <MDBCol
                        xs="12"
                        sm="6"
                        md="4"
                        xl="3"
                        key={`skeleton-${idx}`}
                        className="mb-4"
                      >
                        <Skeleton height={220} style={{ borderRadius: 16 }} />
                      </MDBCol>
                    ))
                  )}
                  {!tableLoading && filteredCatalogs?.length === 0 && (
                    <MDBCol xs="12" className="text-center text-muted py-4">
                      {t("noCatalogToDisplay")}
                    </MDBCol>
                  )}
                  {!tableLoading && filteredCatalogs?.map((catalog) => (
                    <MDBCol
                      xs="12"
                      sm="6"
                      md="4"
                      xl="3"
                      key={catalog._id}
                      className="mb-4"
                    >
                      <MDBCard
                        className="h-100 position-relative border-0"
                        style={{
                          borderRadius: "16px",
                          boxShadow: "0 2px 16px #F0F4F7",
                          overflow: "hidden",
                          transition:
                            "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                          border: "1px solid rgba(0, 0, 0, 0.06)",
                          backgroundColor: "#fefefe",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 32px #F0F4F7";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 16px #F0F4F7";
                        }}
                      >
                        {/* Sync Status Badge */}
                        <div
                          style={{
                            position: "absolute",
                            top: "12px",
                            right: "12px",
                            zIndex: 10,
                          }}
                        >
                          <span
                            className="badge rounded-pill"
                            style={{
                              backgroundColor:
                                catalog.sync_status === true || catalog.sync_status === "synced"
                                  ? "#E6F4EA"
                                  : "#FDEAEA",
                              color: catalog.sync_status === true || catalog.sync_status === "synced"
                              ? "#27AE60"
                              : "#EB5757",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              padding: "4px 10px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {catalog.sync_status === true || catalog.sync_status === "synced"
                              ? t("synced")
                              : t("notsynced")}
                          </span>
                        </div>

                        <MDBCardBody className="p-0 d-flex flex-column h-100">
                          <div className="p-4 pb-3 flex-grow-1">
                            {/* Name & Status Badge in one row */}
                            <div className="mb-2">
                              <CatalogRowTitle
                                catalog={catalog}
                                isArabic={isArabic}
                                t={t}
                                variant="card"
                              />
                            </div>

                            {/* Description */}
                            <p
                              className="mb-2"
                              style={{
                                fontSize: "0.92rem",
                                lineHeight: "1.65",
                                color: "#666",
                                fontWeight: 400,
                              }}
                            >
                              {t("Description")}:{" "}
                              {getCatalogDisplayDescription(catalog, isArabic, t)}
                            </p>

                            {/* Branch */}
                            <p
                              className="mb-1"
                              style={{
                                fontSize: "0.85rem",
                                color: "#444",
                                fontWeight: 500,
                              }}
                            >
                              {t("Store")}:{" "}
                              <span style={{ fontWeight: 400, color: "#666" }}>
                                {catalog.store?.name || "-"}
                              </span>
                            </p>

                            {/* Item Count */}
                            <p
                              className="mb-2"
                              style={{
                                fontSize: "0.85rem",
                                color: "#444",
                                fontWeight: 500,
                              }}
                            >
                              {t("Items")}:{" "}
                              <span style={{ fontWeight: 400, color: "#666" }}>
                                {catalog.item_count}
                              </span>
                            </p>

                            {/* Date and Location */}
                            <div className="mb-0">
                              <h6
                                className="mb-1"
                                style={{
                                  color: "#2c2c2c",
                                  fontWeight: 600,
                                  fontSize: "1.05rem",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {catalog.createdAt ? new Date(catalog.createdAt).toLocaleDateString("en-GB") : "-"}{" "}
                                {catalog.currency}
                              </h6>
                              <small
                                style={{
                                  fontSize: "0.82rem",
                                  color: "#888",
                                  fontWeight: 400,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {catalog.location}
                              </small>
                            </div>
                          </div>

                          {/* Footer */}
                          <div
                            className="px-4 py-3 d-flex justify-content-end align-items-center mt-auto"
                            style={{
                              backgroundColor: "",
                              borderTop: "1px solid rgba(0, 0, 0, 0.05)",
                            }}
                          >
                            <div className="d-flex flex-nowrap gap-2 justify-content-end">
                              {renderCatalogRowActions(catalog)}
                            </div>
                          </div>
                        </MDBCardBody>
                      </MDBCard>
                    </MDBCol>
                  ))}
                </MDBRow>
                <CatalogPaginationFooter
                  show={(filteredCatalogs?.length ?? 0) > 0}
                  t={t}
                  isRtl={isArabic}
                  paginationItems={catalogPaginationItems}
                  currentPage={currentPage}
                  pagination={pagination}
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                  handlePageChange={handlePageChange}
                />
              </div>
            ) : (
                <DataTable
                  tableId="catalog-list"
                  title={t("catalog")}
                  columns={catalogListColumns}
                  data={filteredCatalogs ?? []}
                  rowKey="_id"
                  loading={tableLoading}
                  emptyState={t("noCatalogToDisplay")}
                  primaryAction={
                    canAddCatalog
                      ? {
                          label: t("newCatalog"),
                          onClick: () => setModalOpen(true),
                        }
                      : {
                          label: t("newCatalog"),
                          onClick: () => {},
                          disabled: true,
                          title: t("noPermissionToAccessThis"),
                        }
                  }
                  isRtl={isArabic}
                  sort={{
                    column: tableSortColumn,
                    direction: catalogSortDirection,
                    onChange: ({ column, direction }) =>
                      applyTableSort(column, direction),
                  }}
                  pagination={{
                    page: currentPage,
                    pageSize: itemsPerPage,
                    total: pagination.total,
                    onPageChange: handlePageChange,
                    onPageSizeChange: handleItemsPerPageChange,
                    pageSizeOptions: [10, 20, 50],
                    showLabel: t("show"),
                    ofLabel: t("of", { defaultValue: "of" }),
                    resultsLabel: t("results", { defaultValue: "results" }),
                    isRtl: isArabic,
                    previousLabel: t("previous"),
                    nextLabel: t("next"),
                  }}
                />
            )}
          </>
        )}
        <CatalogModal
          show={Boolean(modalOpen2 && canEditCatalog)}
          setShow={setModalOpen2}
          isEditMode={true}
          editData={selectedCatalog2}
          onCatalogUpdate={() => fetchCatalogs()}
        />
        <DeleteModal
          text={t("areyousureyouwanttodeletethiscatalog")}
          showModal={Boolean(showDeleteModal && canDeleteCatalog)}
          confirmDelete={handleDeleteCatalog}
          onClose={handleCloseModal}
        />
      </MDBContainer>
    </div>
  );
}

export default Catalog;
